import ArrowForward from '@mui/icons-material/ArrowForward';
import InfoOutlined from '@mui/icons-material/InfoOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import Typography from '@mui/material/Typography';
import { RutterItemRuleLineKind, SettlementExportSide } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { SmallLoadingButton } from '~components/Order/ordersDispatchStyledComponents';
import useRutter from '~hooks/useRutter/useRutter';
import useRutterItemRulesQuery from '~queries/useRutterItemRulesQuery';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';

import ItemRuleSection from './ItemRuleSection';
import { ITEM_RULE_SECTIONS } from './itemRuleSections';
import { useQuickBooksMappingQueries } from './QuickBooksMappingQueryContext';

const I18N_PREFIX = 'administration.integration.rutter.quick_books_online.item_rules';

// Drafting is an LLM call over recent lines; stop waiting well past its normal runtime.
const SUGGESTION_TIMEOUT_MS = 60000;

const SIDE_TABS = [
  {
    value: SettlementExportSide.RECEIVABLE,
    label: t(`${I18N_PREFIX}.sides.receivable`),
  },
  { value: SettlementExportSide.PAYABLE, label: t(`${I18N_PREFIX}.sides.payable`) },
];

type DraftKey = `${SettlementExportSide}:${RutterItemRuleLineKind}`;

const toDraftKey = (
  side: SettlementExportSide,
  lineKind: RutterItemRuleLineKind,
): DraftKey => `${side}:${lineKind}`;

interface ItemRulesTabProps {
  onDirtyChange: (isDirty: boolean) => void;
  onOpenDirectMapping: () => void;
}

export default function ItemRulesTab({
  onDirtyChange,
  onOpenDirectMapping,
}: ItemRulesTabProps) {
  const { connectionId, isManagedByOwner } = useQuickBooksMappingQueries();
  const { toasterStore } = useStores();
  const { generateItemRule, upsertItemRule } = useRutter();

  const [side, setSide] = useState(SettlementExportSide.RECEIVABLE);
  const [drafts, setDrafts] = useState<Partial<Record<DraftKey, string>>>({});
  const [dismissedSuggestions, setDismissedSuggestions] = useState<
    Partial<Record<DraftKey, string>>
  >({});
  const [isSaving, setIsSaving] = useState(false);
  // the rule's last-updated time when drafting started; a newer one means the draft landed
  const [draftingBaselines, setDraftingBaselines] = useState<
    Partial<Record<DraftKey, number>>
  >({});

  const rulesQuery = useRutterItemRulesQuery({ connectionId });
  const rules = rulesQuery.data;

  const rulesByDraftKey = useMemo(() => {
    return new Map(
      (rules ?? []).map((rule) => [toDraftKey(rule.side, rule.lineKind), rule]),
    );
  }, [rules]);

  useEffect(() => {
    setDraftingBaselines((previous) => {
      const stillDrafting = Object.fromEntries(
        Object.entries(previous).filter(([draftKey, baseline]) => {
          const rule = rulesByDraftKey.get(draftKey as DraftKey);

          return (rule?.updatedAt.valueOf() ?? 0) <= baseline;
        }),
      );

      return Object.keys(stillDrafting).length === Object.keys(previous).length
        ? previous
        : stillDrafting;
    });
  }, [rulesByDraftKey]);

  const isDrafting = Object.keys(draftingBaselines).length > 0;

  useEffect(() => {
    if (!isDrafting) {
      return;
    }

    // the broadcast may never arrive (dropped connection); fall back to a plain reload
    const giveUpTimer = window.setTimeout(() => {
      setDraftingBaselines({});
      void rulesQuery.refetch();
    }, SUGGESTION_TIMEOUT_MS);

    return () => window.clearTimeout(giveUpTimer);
  }, [isDrafting, rulesQuery.refetch]);

  const savedInstructions = useCallback(
    (draftKey: DraftKey) => rulesByDraftKey.get(draftKey)?.instructions ?? '',
    [rulesByDraftKey],
  );

  const isDraftDirty = useCallback(
    (draftKey: DraftKey) => {
      const draft = drafts[draftKey];

      return draft !== undefined && draft.trim() !== savedInstructions(draftKey).trim();
    },
    [drafts, savedInstructions],
  );

  const dirtyDraftKeys = useMemo(() => {
    return (Object.keys(drafts) as DraftKey[]).filter(isDraftDirty);
  }, [drafts, isDraftDirty]);
  const isDirty = dirtyDraftKeys.length > 0;
  const dirtyDraftKeysForSide = dirtyDraftKeys.filter((draftKey) =>
    draftKey.startsWith(`${side}:`),
  );

  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  useEffect(() => {
    if (!isDirty) {
      return;
    }

    const warnBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };
    window.addEventListener('beforeunload', warnBeforeUnload);

    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [isDirty]);

  const valueFor = (draftKey: DraftKey) =>
    drafts[draftKey] ?? savedInstructions(draftKey);

  const setDraft = (draftKey: DraftKey, instructions: string) => {
    setDrafts((previous) => ({ ...previous, [draftKey]: instructions }));
  };

  const handleSuggest = async (lineKind: RutterItemRuleLineKind) => {
    if (!connectionId) {
      return;
    }

    const draftKey = toDraftKey(side, lineKind);

    setDraftingBaselines((previous) => ({
      ...previous,
      [draftKey]: rulesByDraftKey.get(draftKey)?.updatedAt.valueOf() ?? 0,
    }));
    const isEnqueued = await generateItemRule(connectionId, lineKind, side);

    if (!isEnqueued) {
      setDraftingBaselines((previous) => {
        const remaining = { ...previous };
        delete remaining[draftKey];

        return remaining;
      });
    }
  };

  const handleSave = async () => {
    if (!connectionId || isSaving) {
      return;
    }

    setIsSaving(true);

    try {
      const results = await Promise.all(
        dirtyDraftKeysForSide.map(async (draftKey) => {
          const lineKind = draftKey.split(':')[1] as RutterItemRuleLineKind;
          const instructions = (drafts[draftKey] ?? '').trim();
          const savedRule = await upsertItemRule(connectionId, {
            side,
            line_kind: lineKind,
            instructions: instructions || null,
          });

          return { draftKey, isSaved: savedRule !== null };
        }),
      );
      const savedDraftKeys = results
        .filter((result) => result.isSaved)
        .map((result) => result.draftKey);

      await rulesQuery.refetch();
      setDrafts((previous) => {
        const remaining = { ...previous };
        savedDraftKeys.forEach((draftKey) => delete remaining[draftKey]);

        return remaining;
      });

      if (savedDraftKeys.length > 0) {
        toasterStore.push(alert(t(`${I18N_PREFIX}.saved`), AlertTypes.success));
      }
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Tabs
          onChange={(__, nextSide: SettlementExportSide) => setSide(nextSide)}
          value={side}
        >
          {SIDE_TABS.map((tab) => (
            <Tab key={tab.value} label={tab.label} value={tab.value} />
          ))}
        </Tabs>
      </Box>

      <Box
        sx={{
          alignItems: 'center',
          color: 'text.secondary',
          display: 'flex',
          flexWrap: 'wrap',
          fontSize: '13px',
          gap: 0.75,
          mb: 2,
        }}
      >
        <InfoOutlined fontSize="inherit" />
        <Typography sx={{ fontWeight: 600 }} variant="inherit">
          {t(`${I18N_PREFIX}.how_picked.title`)}
        </Typography>
        <span>{t(`${I18N_PREFIX}.how_picked.instructions`)}</span>
        <ArrowForward fontSize="inherit" />
        <span>
          {t(`${I18N_PREFIX}.how_picked.step_prefix`)}{' '}
          <Button
            onClick={onOpenDirectMapping}
            size="small"
            sx={{
              fontSize: 'inherit',
              minWidth: 0,
              p: 0,
              textDecoration: 'underline',
              textTransform: 'none',
              verticalAlign: 'baseline',
            }}
            variant="text"
          >
            {t(`${I18N_PREFIX}.tabs.direct_mapping`)}
          </Button>
        </span>
        <ArrowForward fontSize="inherit" />
        <span>{t(`${I18N_PREFIX}.how_picked.left_blank`)}</span>
      </Box>

      {rulesQuery.isLoading && (
        <Box
          data-test-id="item-rules-loading"
          sx={{ display: 'flex', justifyContent: 'center', py: 4 }}
        >
          <CircularProgress />
        </Box>
      )}

      {ITEM_RULE_SECTIONS.map((section) => {
        const draftKey = toDraftKey(side, section.lineKind);
        const rule = rulesByDraftKey.get(draftKey) ?? null;
        const hasNewSuggestedInstructions = Boolean(
          rule?.suggestedInstructions &&
          rule.suggestedInstructions !== valueFor(draftKey),
        );
        const hasNoSuggestionReason = Boolean(
          !rule?.suggestedInstructions && rule?.suggestedAt && rule.suggestionReason,
        );
        const hasSuggestionOutcome = hasNewSuggestedInstructions || hasNoSuggestionReason;
        const hasUnseenSuggestion =
          hasSuggestionOutcome &&
          dismissedSuggestions[draftKey] !== rule?.suggestedAt?.toISOString();

        return (
          <ItemRuleSection
            isDisabled={
              isSaving || rulesQuery.isLoading || !connectionId || isManagedByOwner
            }
            isSuggesting={draftingBaselines[draftKey] !== undefined}
            key={section.lineKind}
            onChange={(instructions) => setDraft(draftKey, instructions)}
            onDismissSuggestion={() =>
              setDismissedSuggestions((previous) => ({
                ...previous,
                [draftKey]: rule?.suggestedAt?.toISOString() ?? '',
              }))
            }
            onSuggest={() => handleSuggest(section.lineKind)}
            onUseSuggestion={(instructions) => setDraft(draftKey, instructions)}
            placeholderKey={section.placeholderKey}
            suggestion={hasUnseenSuggestion ? rule : null}
            titleKey={section.titleKey}
            value={valueFor(draftKey)}
          />
        );
      })}

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <SmallLoadingButton
          color="brandV2Yellow"
          disabled={dirtyDraftKeysForSide.length === 0 || isManagedByOwner}
          loading={isSaving}
          onClick={handleSave}
        >
          {t('actions.save')}
        </SmallLoadingButton>
      </Box>
    </>
  );
}
